import { AlertCircle, LoaderCircle, Unlock } from "lucide-react";
import { useState } from "react";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import { type BloqueoDetalle, type PuntoDeBloqueo, useRetirarPunto } from "@/lib/lotos";
import { OfflinePolicyError } from "@/lib/offline-policy";

/**
 * RetiroPunto identifica QUÉ se va a retirar: un punto concreto dentro de su candado (#245).
 * El candado viaja entero porque el diálogo nombra su código y necesita saber cuántos puntos
 * le quedan cubiertos para avisar del último.
 */
export type RetiroPunto = {
  bloqueo: BloqueoDetalle;
  punto: PuntoDeBloqueo;
};

// mensajeError traduce el fallo de retiro a un texto en español. Un retiro no permitido
// (no eres quien colocó el candado ni un supervisor autorizado) es el caso de negocio esperado.
function mensajeError(err: unknown): string {
  // Guarda de política offline (retiro de un punto de candado ajeno sin red): trae su mensaje.
  if (err instanceof OfflinePolicyError) {
    return err.message;
  }
  if (err instanceof ApiError) {
    if (err.code === "RETIRO_NO_PERMITIDO") {
      return "No puedes retirar este candado: debes ser quien lo colocó o un supervisor autorizado.";
    }
    if (err.code === "NO_ENCONTRADO") {
      // El backend usa el mismo código para el bloqueo inexistente y para el punto que ese
      // candado no cubre: el mensaje no debe afirmar cuál de los dos falta.
      return "El candado o el punto ya no existen en el servidor.";
    }
    return err.message || "No se pudo retirar el punto.";
  }
  return "No se pudo retirar el punto. Comprueba tu conexión.";
}

/**
 * RetirarDialog confirma la retirada de UN punto de aislamiento cubierto por un candado
 * (#245). Ofrece un motivo opcional (enriquece la auditoría, sobre todo en un retiro por
 * override) y avisa cuando el punto es el ÚLTIMO activo: al retirarlo, el servidor libera el
 * candado en la misma transacción. Traduce el 403 RETIRO_NO_PERMITIDO a un aviso claro. Al
 * retirar con éxito cierra y la ficha se refresca por invalidación.
 */
export function RetirarDialog({
  retiro,
  idLoto,
  onClose,
}: {
  retiro: RetiroPunto | null;
  idLoto: number;
  onClose: () => void;
}) {
  const obraId = useObraId();
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const retirar = useRetirarPunto(obraId);

  // Último punto ⇒ retirarlo libera el candado. Es la consecuencia que hay que anunciar.
  const esUltimo = (retiro?.bloqueo.puntos.filter((p) => p.activo).length ?? 0) <= 1;

  function cerrar() {
    setMotivo("");
    setError(null);
    onClose();
  }

  async function confirmar() {
    if (!retiro) {
      return;
    }
    setError(null);
    try {
      await retirar.mutateAsync({
        idBloqueo: retiro.bloqueo.id,
        idPunto: retiro.punto.id_punto,
        idLoto,
        motivo: motivo.trim() === "" ? undefined : motivo.trim(),
      });
      cerrar();
    } catch (err) {
      setError(mensajeError(err));
    }
  }

  return (
    <Modal open={retiro !== null} onClose={cerrar} title="Retirar punto">
      <p className="text-base text-slate-600">
        ¿Retirar el punto <strong>{retiro?.punto.nombre}</strong> del candado{" "}
        <strong>{retiro?.bloqueo.codigo_candado}</strong>? Solo debe hacerse tras confirmar que es
        seguro.
      </p>

      {retiro && esUltimo ? (
        <Alert>
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            Es el último punto: al retirarlo el candado {retiro.bloqueo.codigo_candado} quedará
            libre.
          </span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="motivo">Motivo (opcional)</Label>
        <Textarea
          id="motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Justificación del retiro (útil en un retiro por override)."
        />
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </Alert>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={cerrar} disabled={retirar.isPending}>
          Cancelar
        </Button>
        <Button variant="destructive" onClick={() => void confirmar()} disabled={retirar.isPending}>
          {retirar.isPending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Unlock className="h-5 w-5" aria-hidden="true" />
          )}
          Retirar
        </Button>
      </div>
    </Modal>
  );
}
